/**
 * Company & Permission Type Definitions
 *
 * Core types for the company/team system, role hierarchy, and permission management.
 *
 * Role Hierarchy (highest to lowest):
 *   Admin/Owner  → System-level (developer/staff only, premium exempt)
 *   Manager      → Company owner, full control of their company
 *   Editor       → Configurable permissions within company (set by Manager)
 *   User         → Form filler only (sees own submissions/approvals)
 *
 * Key rule: Subscription tier ALWAYS overrides role permissions.
 */

import { Timestamp } from 'firebase/firestore';

// ==================== ROLE TYPES ====================

export type UserRole = 'Admin' | 'Owner' | 'Manager' | 'Editor' | 'User';

/** Roles that bypass subscription checks entirely */
export const SYSTEM_ADMIN_ROLES: UserRole[] = ['Admin', 'Owner'];

/** Roles that can manage a company */
export const COMPANY_MANAGER_ROLES: UserRole[] = ['Admin', 'Owner', 'Manager'];

// ==================== EDITOR PERMISSIONS ====================

export interface EditorPermissions {
  canEditForms: boolean;
  canViewSubmissions: boolean;
  canShareForms: boolean;
  canArchiveSubmissions: boolean;
  canViewAnalytics: boolean;
  canExportData: boolean;
  canManageApprovals: boolean;
  canUseLandingPage: boolean;
  canViewSecureDetails: boolean;
}

export const DEFAULT_EDITOR_PERMISSIONS: EditorPermissions = {
  canEditForms: true,
  canViewSubmissions: true,
  canShareForms: true,
  canArchiveSubmissions: true,
  canViewAnalytics: true,
  canExportData: true,
  canManageApprovals: false,
  canUseLandingPage: false,
  canViewSecureDetails: false,
};

// ==================== COMPANY MEMBER ====================

export interface CompanyMember {
  userId: string;
  email: string;
  name?: string;
  role: 'Manager' | 'Editor' | 'User';
  joinedAt: Timestamp | Date;
}

// ==================== JOIN REQUEST ====================

export interface JoinRequest {
  userId: string;
  email: string;
  name: string;
  /** Set when the request was created; omitted on legacy requests */
  emailVerified?: boolean;
  requestedAt: Timestamp | Date;
}

// ==================== COMPANY ====================

export interface Company {
  id: string;
  name: string;
  slug: string;
  logo?: string;
  companyCode: string;
  ownerId: string;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  subscriptionTier: 'free' | 'pro' | 'proPlus' | 'enterprise';
  editorPermissions: EditorPermissions;
  members: CompanyMember[];
  pendingRequests: JoinRequest[];
}

// ==================== PERMISSION TYPES ====================

export type Permission =
  | 'viewAllForms'
  | 'editForms'
  | 'viewSubmissions'
  | 'shareForms'
  | 'archiveSubmissions'
  | 'viewAnalytics'
  | 'exportData'
  | 'manageApprovals'
  | 'useLandingPage'
  | 'manageMembers'
  | 'manageCompany'
  | 'adminPanel'
  | 'viewSecureDetails';

// ==================== CREATE COMPANY INPUT ====================

export interface CreateCompanyInput {
  name: string;
  slug: string;
  logo?: string;
  ownerId: string;
  ownerEmail: string;
  ownerName?: string;
}
